import { onboardingExchangeTokenAppOnboardingExchangeTokenPost } from '@/client/sdk.gen'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useOnboardingStepViewed } from '@/hooks/use-analytics'
import { useAuth } from '@/hooks/use-auth'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

interface ActivatedSearch {
  error?: string
  exchange_token?: string
}

export const Route = createFileRoute('/onboarding/activated')({
  component: ActivatedPage,
  validateSearch: (search: Record<string, unknown>): ActivatedSearch => ({
    error: search['error'] as string | undefined, // eslint-disable-line dot-notation
    exchange_token: search['exchange_token'] as string | undefined, // eslint-disable-line dot-notation
  }),
})

const ERROR_MESSAGES: Record<
  string,
  { title: string, message: string, action: { label: string, to: string } }
> = {
  expired: {
    title: 'Link expired',
    message: 'This verification link has expired. Please complete onboarding again.',
    action: { label: 'Start over', to: '/onboarding' },
  },
  used: {
    title: 'Link already used',
    message: 'This verification link has already been used.',
    action: { label: 'Sign in', to: '/login' },
  },
  invalid: {
    title: 'Invalid link',
    message: 'This verification link is invalid. Please complete onboarding again.',
    action: { label: 'Start over', to: '/onboarding' },
  },
  already_exists: {
    title: 'Account exists',
    message: 'An account with this email already exists.',
    action: { label: 'Sign in', to: '/login' },
  },
  exchange_failed: {
    title: 'Authentication failed',
    message: 'We couldn\'t complete your sign-in. Please try signing in manually.',
    action: { label: 'Sign in', to: '/login' },
  },
}

function ActivatedPage() {
  useOnboardingStepViewed('activated')
  const { error, exchange_token } = Route.useSearch()
  const [exchangeError, setExchangeError] = useState<string | undefined>()
  const exchangeStartedRef = useRef(false)
  const { markSessionEstablished } = useAuth()

  useEffect(() => {
    if (!exchange_token || exchangeStartedRef.current)
      return

    exchangeStartedRef.current = true
    onboardingExchangeTokenAppOnboardingExchangeTokenPost({
      query: { token: exchange_token },
    }).then(async ({ error: respError }) => {
      if (respError) {
        setExchangeError('exchange_failed')
        return
      }
      // Sync both the localStorage flag and React `hasSession` state with
      // the cookie the exchange endpoint just set. The hard reload below
      // would also remount AuthProvider and pick up localStorage on its
      // own, but going through markSessionEstablished() centralizes the
      // invariant in one place — so future contributors who optimize
      // away the reload don't re-introduce the demoday-style logout bug.
      await markSessionEstablished()
      globalThis.location.href = '/accounts?from=onboarding'
    }).catch(() => {
      setExchangeError('exchange_failed')
    })
  }, [exchange_token, markSessionEstablished])

  // Show loading while exchanging token
  if (exchange_token && !exchangeError) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Setting up your account...</span>
        </div>
      </div>
    )
  }

  const errorKey = exchangeError || error
  if (!errorKey) {
    return <Navigate to="/onboarding" />
  }

  const errorInfo = ERROR_MESSAGES[errorKey] || {
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
    action: { label: 'Start over', to: '/onboarding' },
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-2xl">{errorInfo.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">{errorInfo.message}</p>
          <Button asChild className="w-full">
            <a href={errorInfo.action.to}>{errorInfo.action.label}</a>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
